import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFormikContext } from 'formik';

import { Grid, Typography, FormControl, Autocomplete } from '@mui/material';

import PropTypes from 'prop-types';
import { OwnerFormFields } from '../OwnerForm';
import { ImageUploadArea } from '../../../../components/ImageUploadArea';
import { FormLabelStyled, TextFieldStyled } from './CreateOwnerForm.styles';

const CreateOwnerForm = props => {
  const { onLogoChange } = props;

  // uses
  const intl = useIntl();
  const { values, errors, touched, setFieldValue } = useFormikContext();

  const instancesSelectItems = React.useMemo(() => {
    return [
      {
        value: 'insurance-brydge',
        element: 'InsuranceBrydge',
      },
      {
        value: 'soderberg',
        element: 'Soderberg',
      },
    ];
  }, []);

  // events
  const handleChange = field => event => {
    setFieldValue(field, event.target.value);
  };

  const handleLogoChange = React.useCallback(
    file => {
      setFieldValue('logo', file || '');
      onLogoChange?.(file);
    },
    [setFieldValue, onLogoChange],
  );

  // template
  return (
    <Grid sx={{ width: '100%' }} container spacing={2}>
      {/* OWNER DATA */}
      <Grid item xs={12}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          <FormattedMessage id="owners.ownerData" />
        </Typography>
      </Grid>

      {/* name */}
      <Grid item xs={12}>
        <FormLabelStyled>
          <FormattedMessage id="owners.name" />
          {` *`}
        </FormLabelStyled>
        <TextFieldStyled
          color="secondary"
          error={touched.ownerName && !!errors.ownerName}
          fullWidth
          helperText={touched.ownerName && errors.ownerName ? errors.ownerName : null}
          name="ownerName"
          placeholder={intl.formatMessage({ id: 'owners.name' }).toUpperCase()}
          value={values.ownerName}
          variant="outlined"
          onChange={handleChange('ownerName')}
        />
      </Grid>

      {/* address / city / zipcode */}
      <Grid item xs={4}>
        <FormLabelStyled>
          <FormattedMessage id="owners.address" />
          {` *`}
        </FormLabelStyled>
        <TextFieldStyled
          color="secondary"
          error={touched.address && !!errors.address}
          fullWidth
          helperText={touched.address && errors.address ? errors.address : null}
          name="address"
          placeholder={intl.formatMessage({ id: 'owners.address' }).toUpperCase()}
          value={values.address}
          variant="outlined"
          onChange={handleChange('address')}
        />
      </Grid>
      <Grid item xs={4}>
        <FormLabelStyled>
          <FormattedMessage id="owners.zipCode" />
        </FormLabelStyled>
        <TextFieldStyled
          color="secondary"
          fullWidth
          name="zipCode"
          placeholder={intl.formatMessage({ id: 'owners.zipCode' }).toUpperCase()}
          value={values.zipCode}
          variant="outlined"
          onChange={handleChange('zipCode')}
        />
      </Grid>
      <Grid item xs={4}>
        <FormLabelStyled>
          <FormattedMessage id="owners.city" />
        </FormLabelStyled>
        <TextFieldStyled
          color="secondary"
          fullWidth
          name="city"
          placeholder={intl.formatMessage({ id: 'owners.city' }).toUpperCase()}
          value={values.city}
          variant="outlined"
          onChange={handleChange('city')}
        />
      </Grid>

      {/* email / phone */}
      <Grid item xs={6}>
        <FormLabelStyled>
          <FormattedMessage id="owners.ownerEmail" />
        </FormLabelStyled>
        <TextFieldStyled
          color="secondary"
          error={touched.ownerEmail && !!errors.ownerEmail}
          fullWidth
          helperText={touched.ownerEmail && errors.ownerEmail ? errors.ownerEmail : null}
          name="ownerEmail"
          placeholder={intl.formatMessage({ id: 'owners.email' }).toUpperCase()}
          type="email"
          value={values.ownerEmail}
          variant="outlined"
          onChange={handleChange('ownerEmail')}
        />
      </Grid>
      <Grid item xs={6}>
        <FormLabelStyled>
          <FormattedMessage id="owners.ownerPhone" />
        </FormLabelStyled>
        <TextFieldStyled
          color="secondary"
          fullWidth
          name="ownerPhone"
          placeholder={intl.formatMessage({ id: 'owners.ownerPhone' }).toUpperCase()}
          value={values.ownerPhone}
          variant="outlined"
          onChange={handleChange('ownerPhone')}
        />
      </Grid>

      {/* CONTACT INFORMATION */}
      <Grid item xs={12}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          <FormattedMessage id="owners.contactInformation" />
        </Typography>
      </Grid>

      {/* contact name */}
      <Grid item xs={12}>
        <FormLabelStyled>
          <FormattedMessage id="owners.contactName" />
          {` *`}
        </FormLabelStyled>
        <TextFieldStyled
          color="secondary"
          error={touched.contactName && !!errors.contactName}
          helperText={touched.contactName && errors.contactName ? errors.contactName : null}
          fullWidth
          name="contactName"
          placeholder={intl.formatMessage({ id: 'owners.contactName' }).toUpperCase()}
          value={values.contactName}
          variant="outlined"
          onChange={handleChange('contactName')}
        />
      </Grid>

      {/* contact email / phone */}
      <Grid item xs={6}>
        <FormLabelStyled>
          <FormattedMessage id="owners.contactEmail" />
          {` *`}
        </FormLabelStyled>
        <TextFieldStyled
          color="secondary"
          error={touched.contactEmail && !!errors.contactEmail}
          helperText={touched.contactEmail && errors.contactEmail ? errors.contactEmail : null}
          fullWidth
          name="contactEmail"
          placeholder={intl.formatMessage({ id: 'owners.contactEmail' }).toUpperCase()}
          type="email"
          value={values.contactEmail}
          variant="outlined"
          onChange={handleChange('contactEmail')}
        />
      </Grid>
      <Grid item xs={6}>
        <FormLabelStyled>
          <FormattedMessage id="owners.contactPhone" />
        </FormLabelStyled>
        <TextFieldStyled
          color="secondary"
          fullWidth
          name="contactPhone"
          placeholder={intl.formatMessage({ id: 'owners.contactPhone' }).toUpperCase()}
          value={values.contactPhone}
          variant="outlined"
          onChange={handleChange('contactPhone')}
        />
      </Grid>

      {/* OWNER SETTINGS */}
      <Grid item xs={12}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          <FormattedMessage id="owners.ownerSettings" />
        </Typography>
      </Grid>

      {/* logo */}
      <Grid item xs={4}>
        <ImageUploadArea label={OwnerFormFields.Logo.Label} onChange={handleLogoChange} />
      </Grid>

      {/* instance */}
      <Grid item xs={4}>
        <FormLabelStyled>
          <FormattedMessage id="global.pensioenbijIntegratie" />
        </FormLabelStyled>
        <FormControl fullWidth>
          <Autocomplete
            id="instance"
            options={instancesSelectItems}
            getOptionLabel={option => option.element}
            value={instancesSelectItems.find(item => item.value === values.instance) || null}
            onChange={(event, newValue) => {
              setFieldValue('instance', newValue?.value || '');
            }}
            renderInput={params => (
              <TextFieldStyled
                {...params}
                color="secondary"
                name="instance"
                placeholder={intl.formatMessage({ id: 'global.pensioenbijIntegratie' }).toUpperCase()}
                variant="outlined"
              />
            )}
          />
        </FormControl>
      </Grid>

      {/* kvkNumber */}
      <Grid item xs={4}>
        <FormLabelStyled>
          <FormattedMessage id="global_kvkNumber" />
        </FormLabelStyled>
        <TextFieldStyled
          color="secondary"
          error={touched.kvkNumber && !!errors.kvkNumber}
          fullWidth
          helperText={touched.kvkNumber && errors.kvkNumber ? errors.kvkNumber : null}
          name="kvkNumber"
          placeholder={intl.formatMessage({ id: 'global_kvkNumber' }).toUpperCase()}
          value={values.kvkNumber}
          variant="outlined"
          onChange={handleChange('kvkNumber')}
        />
      </Grid>
    </Grid>
  );
};

CreateOwnerForm.propTypes = {
  onLogoChange: PropTypes.func,
};

export default CreateOwnerForm;
